import {
  BrowserClient,
  Severity as SentrySeverity,
  getCurrentHub,
  Hub,
  Integrations,
} from '@sentry/browser';
import { Hub as HubInterface, Integration, Scope } from '@sentry/types';
import {
  ExceptionsService,
  ClientOptions,
  User,
  Options,
  Extra,
  Tags,
  Breadcrumb,
  Context,
} from './ExceptionsService';
import { Severity } from './Severity';
import { BackendError } from '../backend/BackendError';
import { extendsNetworkError } from './network';

export const defaultIntegrations: Integration[] = [
  new Integrations.LinkedErrors(),
  new Integrations.UserAgent(),
  new Integrations.Dedupe(),
];

interface NormalizeErrorParams {
  error: Error | BackendError | string;
  options?: Options;
}

export class SentryExceptionsService implements ExceptionsService {
  readonly #hub: HubInterface;

  constructor(hub: HubInterface) {
    this.#hub = hub;
  }

  static from({
    dsn,
    release,
    environment,
    application,
    integrations,
  }: ClientOptions): ExceptionsService {
    const transportOptions = {
      dsn,
      fetchParameters: {
        keepalive: true as unknown as string,
      },
    };

    const clientOptions = {
      dsn,
      release,
      environment,
      transportOptions,
      integrations,
    };

    let hub: HubInterface;

    // eslint-disable-next-line no-underscore-dangle
    if (!window.__SENTRY__?.hub) {
      // host application initialization
      const client = new BrowserClient({ ...clientOptions, defaultIntegrations });

      hub = getCurrentHub(); // it also creates the initial hub and put it into window.__SENTRY__

      hub.bindClient(client);
    } else {
      // micro frontend initialization
      const client = new BrowserClient(clientOptions);

      hub = new Hub(client);
    }

    hub.configureScope((scope) => {
      scope.setTag('application', application);
    });

    return new SentryExceptionsService(hub);
  }

  get hub(): HubInterface {
    return this.#hub;
  }

  setUser({ email, id, name }: User): void {
    this.#hub.configureScope((scope) => {
      scope.setUser({
        email,
        id,
        username: name,
      });
    });
  }

  #setTags = (scope: Scope, tags?: Tags): void => {
    if (!tags) {
      return;
    }

    Object.entries(tags).forEach(([key, value]) => {
      scope.setTag(key, value);
    });
  };

  #setExtra = (scope: Scope, extra?: Extra): void => {
    if (!extra) {
      return;
    }

    Object.entries(extra).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
  };

  setContext({ tags, extra }: Context): void {
    // checking for empty values is left from the old logic
    if (!tags && !extra) {
      return;
    }

    this.#hub.configureScope((scope) => {
      this.#setTags(scope, tags);
      this.#setExtra(scope, extra);
    });
  }

  #normalizeError = ({ error, options }: NormalizeErrorParams): NormalizeErrorParams => {
    if (error instanceof Error) {
      return { error, options };
    }

    return {
      /**
       * Sometimes `.captureException(...)` can be invoked with non error value
       * To identify place where it happened
       * Need to create an error to restore stack trace
       */
      error: new Error(
        `Non-Error exception captured with keys: ${
          Object.keys(error).join(',') || '[no keys]'
        }`,
      ),
      options: { ...options, extra: { ...options?.extra, error } },
    };
  };

  captureException(rawError: Error | BackendError, userOptions?: Options): void {
    const { error, options } = this.#normalizeError(
      extendsNetworkError({ error: rawError, options: userOptions }),
    );

    if (!options) {
      this.#hub.captureException(error);

      return;
    }

    const { fingerprint, tags, extra, level = Severity.ERROR } = options;

    this.#hub.withScope((scope) => {
      this.#setTags(scope, tags);
      this.#setExtra(scope, extra);

      if (Array.isArray(fingerprint)) {
        scope.setFingerprint(fingerprint);
      }

      scope.setLevel(
        // @ts-expect-error - type safety granted by test: `./__tests__/Severity.spec.ts`
        level as SentrySeverity,
      );

      this.#hub.captureException(error);
    });
  }

  captureMessage(message: string, options?: Options): void {
    if (!options) {
      this.#hub.captureMessage(message);

      return;
    }

    const { tags, extra, level = Severity.INFO } = options;

    this.#hub.withScope((scope) => {
      this.#setTags(scope, tags);
      this.#setExtra(scope, extra);
      scope.setLevel(
        // @ts-expect-error - type safety granted by test: `./__tests__/Severity.spec.ts`
        level as SentrySeverity,
      );

      this.#hub.captureMessage(message);
    });
  }

  addBreadcrumb({ category, message, level = Severity.INFO }: Breadcrumb): void {
    this.#hub.addBreadcrumb({
      category,
      message,
      // @ts-expect-error - type safety granted by test: `./__tests__/Severity.spec.ts`
      level: level as SentrySeverity,
    });
  }
}
